'use client'

import { Category } from '@/payload-types'
import CategoryCard from './CategoryCard'
import React from 'react'
import classes from './index.module.css'
import cardClasses from './CategoryCard.module.css'
import { Link } from '@/i18n/routing'
import { useTranslations } from 'next-intl'
import { SectionHeader } from '@/components/ui/SectionHeader'

import { cn } from '@/utilities/cn'

type ShopByCategoriesProps = {
  categories: Category[]
  /** How many more categories exist beyond the displayed ones (CAT-3).
   *  When > 0 a "+N mehr" tile is appended to the grid linking to /categories. */
  remaining?: number
}

const ShopByCategories = ({ categories, remaining = 0 }: ShopByCategoriesProps) => {
  const t = useTranslations('product')
  const commonT = useTranslations('common')

  if (!categories || categories.length === 0) {
    return <div className={classes.noCategories}>{t('noCategories')}</div>
  }

  // CAT-4 fix: bento slot positions are index-mapped (0=large, 7=wide).
  // `sort: 'title'` in page.tsx is alphabetical, so any category that happens
  // to be first alphabetically gets the hero slot. Curated slug order pins
  // specific categories to specific bento slots. Slugs not in the list fall
  // back to alphabetical order (preserves Wave 3 behaviour for future adds).
  const BENTO_SLUG_ORDER = [
    'angebote',
    'headshop',
    'flohmarkt',
    'cbd',
    'ethnobotanik',
    'growshop',
    'hanfpflanzen',
    'hanfsamen',
  ] as const

  const orderedCategories = [...categories].sort((a, b) => {
    const ai = BENTO_SLUG_ORDER.indexOf(a.slug as (typeof BENTO_SLUG_ORDER)[number])
    const bi = BENTO_SLUG_ORDER.indexOf(b.slug as (typeof BENTO_SLUG_ORDER)[number])
    if (ai === -1 && bi === -1) return a.title.localeCompare(b.title)
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

  return (
    <div className="w-full max-w-[80rem] mx-auto my-3">
      <SectionHeader
        title={t('categories')}
        href="/categories"
        buttonLabel={commonT('viewAll')}
        testId="categories-title"
      />
      <section
        data-testid="shop-by-categories"
        className="w-full px-3 sm:px-5 lg:px-6 py-3 sm:py-4 lg:py-6 bg-white/5 dark:bg-black/20 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl transition-all duration-300 hover:bg-white/10 dark:hover:bg-black/30"
        style={{
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -40px 80px rgba(0,0,0,0.25), 0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        <div
          data-testid="categories-grid"
          className={cn(classes.bentoGrid, 'px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6')}
        >
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(200px + 6px)', left: '50%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', left: '50%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', left: '25%' }} />
          <span className={`${classes.gridDot} hidden lg:block`} style={{ top: 'calc(400px + 18px)', right: '25%' }} />

          {orderedCategories.map((category, index) => (
            <CategoryCard key={category.id} category={category} index={index} />
          ))}
        </div>
        {/* Audit #20: "+N more" is a full-width strip below the grid so it never
            sits alone in a half-empty last row. */}
        {remaining > 0 && (
          <div className="px-4 sm:px-6 lg:px-8 pt-3 pb-3 sm:pb-4 lg:pb-6">
            <div className={`${cardClasses.cardWrapper} ${cardClasses.moreStrip}`}>
              <Link
                href="/categories"
                className={`${cardClasses.card} ${cardClasses.hoverDiagBR}`}
                data-testid="category-card-more"
                aria-label={t('moreCategories', { count: remaining })}
              >
                <div className={cardClasses.moreContent}>
                  <span className={cardClasses.moreCount}>+{remaining}</span>
                  <span className={cardClasses.moreLabel}>{t('moreCategoriesLabel')}</span>
                  <span className={cardClasses.moreArrow} aria-hidden="true">→</span>
                </div>
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

export default ShopByCategories

